import { useEffect, useRef, useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody } from '@/ui/Card';
import { Drawer } from '@/ui/Drawer';
import { ConfirmDialog } from '@/ui/Dialog';
import { Field, Input, Select, adornoDeSelect, clasesDeControl } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { SelectorDeIcono } from '@/components/SelectorDeIcono';
import { cn } from '@/lib/cn';
import {
  useBorrarTipo, useFuentes, useGuardarTipo, useReporte, useTipo, useTipos,
  type CampoDeFormulario, type PasoDelFlujo, type TipoDeSolicitud, type TipoDeCampo,
} from './api';

const TIPOS_DE_CAMPO: [TipoDeCampo, string][] = [
  ['text', 'Texto'], ['textarea', 'Texto largo'], ['number', 'Número'], ['date', 'Fecha'],
  ['rango_fechas', 'Período (desde–hasta)'], ['email', 'Correo'], ['phone', 'Teléfono'],
  ['select', 'Lista'], ['radio', 'Opción única'], ['checkbox', 'Casillas'],
  ['file', 'Archivo'], ['employee', 'Empleado'],
];

const APROBADORES: [PasoDelFlujo['approver_type'], string][] = [
  ['jefe', 'Jefe inmediato'], ['bienestar', 'Bienestar'],
  ['area_manager', 'Líder de área'], ['final', 'Aprobación final'],
];

/**
 * Controles sueltos para las filas compactas del constructor.
 *
 * No van dentro de un `<Field>` porque su etiqueta ya existe —`sr-only` en las
 * filas, visible en los ajustes— y `Field` pintaría una segunda encima de cada
 * recuadro, en una rejilla donde ya hay doce.
 */
function Entrada({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cn(clasesDeControl, className)} />;
}

function Lista({ className, children, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...props} style={adornoDeSelect}
            className={cn(clasesDeControl, 'appearance-none bg-no-repeat pr-9', className)}>
      {children}
    </select>
  );
}

/* ── El listado ────────────────────────────────────────────────────────── */

export function Tipos() {
  const consulta = useTipos();
  const borrar = useBorrarTipo();
  const [editando, setEditando] = useState<number | null | undefined>(undefined);
  const [reporte, setReporte] = useState<TipoDeSolicitud | null>(null);
  const [borrandoId, setBorrandoId] = useState<number | null>(null);

  if (consulta.isError) {
    return <EstadoError err={consulta.error} que="los tipos de solicitud"
                        onReintentar={() => void consulta.refetch()} />;
  }

  const tipos = consulta.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setEditando(null)}>
          <i className="bi bi-plus-lg" aria-hidden="true" /> Nuevo tipo
        </Button>
      </div>

      {consulta.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-36 w-full rounded-md" />)}
        </div>
      ) : tipos.length === 0 ? (
        <EstadoVacio icono="bi-plus-circle" titulo="Crea tu primer tipo de solicitud"
                     descripcion="Un tipo define qué datos se piden y quién los aprueba."
                     accion={{ texto: 'Nuevo tipo', onClick: () => setEditando(null) }} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {tipos.map((t) => (
            <Card key={t.id}>
              <CardBody className="flex h-full flex-col gap-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <i className={cn('bi', t.icon || 'bi-file-earmark-text', 'text-6 text-muted')}
                       aria-hidden="true" />
                    <p className="mt-1 break-words text-4 font-bold text-brand-ink">{t.name}</p>
                  </div>
                  <Badge tone={t.is_active ? 'ok' : 'neutral'}>
                    {t.is_active ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>
                <p className="text-2 text-muted">
                  {t.field_count} campo{t.field_count === 1 ? '' : 's'} ·{' '}
                  {t.step_count} paso{t.step_count === 1 ? '' : 's'}
                </p>
                <div className="mt-auto flex flex-wrap gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditando(t.id)}>
                    <i className="bi bi-sliders" aria-hidden="true" /> Editar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setReporte(t)}>
                    <i className="bi bi-bar-chart" aria-hidden="true" /> Reporte
                  </Button>
                  <Button size="sm" variant="ghost" aria-label={`Eliminar ${t.name}`}
                          onClick={() => setBorrandoId(t.id)}>
                    <i className="bi bi-trash text-danger-ink" aria-hidden="true" />
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {editando !== undefined && (
        <EditorDeTipo id={editando} onCerrar={() => setEditando(undefined)} />
      )}
      <PanelDeReporte tipo={reporte} onCerrar={() => setReporte(null)} />
      <ConfirmDialog
        abierto={borrandoId != null}
        onCambio={(v) => { if (!v) setBorrandoId(null); }}
        titulo="Eliminar tipo de solicitud"
        descripcion="Si ya tiene solicitudes radicadas no se borra: se desactiva, para no dejar huérfano lo que la gente ya envió."
        textoConfirmar="Eliminar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={async () => {
          try {
            const r = await borrar.mutateAsync(borrandoId!);
            Utils.toast(r?.message || 'Tipo eliminado', 'success');
            setBorrandoId(null);
          } catch (err) {
            Utils.toast(err instanceof Error ? err.message : 'No se pudo eliminar', 'error');
          }
        }}
      />
    </div>
  );
}

/* ── El editor ─────────────────────────────────────────────────────────── */

interface Borrador {
  name: string; icon: string; category: string; recipient_emails: string;
  description: string; is_active: boolean;
  fields: CampoDeFormulario[]; flow: PasoDelFlujo[];
}

const VACIO: Borrador = {
  name: '', icon: 'bi-file-earmark-text', category: '', recipient_emails: '',
  description: '', is_active: true, fields: [], flow: [],
};

function EditorDeTipo({ id, onCerrar }: { id: number | null; onCerrar: () => void }) {
  const consulta = useTipo(id);
  const fuentes = useFuentes(true);
  const guardar = useGuardarTipo();
  const [b, setB] = useState<Borrador>(VACIO);
  const [errorNombre, setErrorNombre] = useState<string | undefined>();
  const sembrado = useRef(false);

  useEffect(() => {
    if (sembrado.current) return;
    if (id == null) { setB(VACIO); sembrado.current = true; return; }
    const t = consulta.data;
    if (!t) return;
    setB({
      name: t.name ?? '', icon: t.icon || 'bi-file-earmark-text',
      category: t.category ?? '', recipient_emails: t.recipient_emails ?? '',
      description: t.description ?? '', is_active: t.is_active !== false,
      // Copia profunda: editar los campos no debe tocar la caché de la consulta.
      fields: JSON.parse(JSON.stringify(t.fields ?? [])),
      flow: JSON.parse(JSON.stringify(t.flow ?? [])),
    });
    sembrado.current = true;
  }, [id, consulta.data]);

  const cat = fuentes.data?.sources ?? [];
  const autos = fuentes.data?.autofill ?? [];

  const set = <K extends keyof Borrador>(k: K, v: Borrador[K]) => setB((p) => ({ ...p, [k]: v }));

  function setCampo(i: number, k: keyof CampoDeFormulario, v: unknown) {
    setB((p) => {
      const fields = p.fields.map((f, j) => (j === i ? { ...f, [k]: v } : f));
      // Si un campo deja de ser fecha, las comparaciones que lo referencian
      // quedan sin sentido: se limpian aquí en vez de dejar que el guardado
      // falle con un error que el administrador no sabría cómo corregir.
      if (k === 'type' && v !== 'date') {
        const huerfana = fields[i]?.key;
        return {
          ...p,
          fields: fields.map((f, j) => (
            f.date_after === huerfana || j === i ? { ...f, date_after: null } : f
          )),
        };
      }
      return { ...p, fields };
    });
  }

  async function enviar() {
    const nombre = b.name.trim();
    if (!nombre) { setErrorNombre('El nombre es obligatorio.'); return; }

    // Generar claves a partir de la etiqueta si faltan. Se guarda el mapa
    // anterior→nueva porque `date_after` apunta a una clave: si un campo se
    // renombra al guardar, la comparación quedaría apuntando al vacío y el
    // formulario dejaría de validar sin que nadie se entere.
    const mapa: Record<string, string> = {};
    const fields = b.fields.map((f, i) => {
      if (f.key && !/^campo_|^c_/.test(f.key)) return { ...f };
      const anterior = f.key;
      const nueva = (f.label || `campo${i}`).toLowerCase()
        .replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || `campo${i}`;
      if (anterior) mapa[anterior] = nueva;
      return { ...f, key: nueva };
    }).map((f) => (f.date_after && mapa[f.date_after]
      ? { ...f, date_after: mapa[f.date_after] } : f));

    const body: Partial<TipoDeSolicitud> = {
      name: nombre,
      icon: b.icon.trim() || 'bi-file-earmark-text',
      category: b.category.trim(),
      recipient_emails: b.recipient_emails.trim(),
      description: b.description.trim(),
      is_active: b.is_active,
      fields,
      flow: b.flow,
      report_columns: [
        { key: 'applicant', label: 'Empleado' },
        { key: 'status', label: 'Estado' },
        { key: 'created_at', label: 'Fecha' },
      ].concat(fields.filter((f) => f.type !== 'file').map((f) => ({ key: f.key, label: f.label }))),
    };
    try {
      await guardar.mutateAsync({ id, body });
      Utils.toast('Tipo guardado', 'success');
      onCerrar();
    } catch (err) {
      Utils.toast(err instanceof Error ? err.message : 'No se pudo guardar', 'error');
    }
  }

  return (
    <Drawer
      abierto
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo={id ? 'Editar tipo de solicitud' : 'Nuevo tipo de solicitud'}
      descripcion="Define qué datos se le piden al colaborador y quién los aprueba."
      ancho="lg"
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button loading={guardar.isPending} onClick={() => void enviar()}>Guardar</Button>
        </>
      }
    >
      {id != null && consulta.isLoading ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nombre" error={errorNombre} id="st-name">
              <Input value={b.name}
                     onChange={(e) => { set('name', e.target.value); setErrorNombre(undefined); }} />
            </Field>
            <SelectorDeIcono id="st-icon" valor={b.icon} onCambio={(v) => set('icon', v)} />
            <Field label="Categoría" opcional id="st-cat">
              <Input value={b.category} onChange={(e) => set('category', e.target.value)} />
            </Field>
            <Field label="Correos de Bienestar" opcional
                   ayuda="Separados por coma. Reciben aviso de cada radicación." id="st-emails">
              <Input value={b.recipient_emails}
                     onChange={(e) => set('recipient_emails', e.target.value)} />
            </Field>
            <Field label="Descripción" opcional className="sm:col-span-2" id="st-desc">
              <Input value={b.description} onChange={(e) => set('description', e.target.value)} />
            </Field>
          </div>

          <label className="flex cursor-pointer items-center gap-2 text-3 text-ink">
            <input type="checkbox" checked={b.is_active}
                   onChange={(e) => set('is_active', e.target.checked)}
                   className="size-4 accent-[var(--color-brand)]" />
            Activo (visible para colaboradores)
          </label>

          <section>
            <h3 className="mb-2 text-3 font-semibold text-brand-ink">Campos del formulario</h3>
            <div className="flex flex-col gap-3">
              {b.fields.map((f, i) => (
                <FilaDeCampo
                  key={i}
                  campo={f}
                  indice={i}
                  todos={b.fields}
                  catalogos={cat}
                  autollenados={autos}
                  onCambio={(k, v) => setCampo(i, k, v)}
                  onBorrar={() => set('fields', b.fields.filter((_, j) => j !== i))}
                />
              ))}
            </div>
            <Button size="sm" variant="secondary" className="mt-3"
                    onClick={() => set('fields', [...b.fields, {
                      key: `campo_${Date.now().toString().slice(-5)}`,
                      label: '', type: 'text', required: false, options: [],
                    }])}>
              <i className="bi bi-plus" aria-hidden="true" /> Agregar campo
            </Button>
          </section>

          <section>
            <h3 className="mb-1 text-3 font-semibold text-brand-ink">Flujo de aprobación</h3>
            <p className="mb-2 text-2 leading-relaxed text-muted">
              Cada paso lo aprueba quien elijas: <strong>Jefe inmediato</strong>,{' '}
              <strong>Bienestar</strong> o <strong>Líder de área</strong>. Para que aprueben{' '}
              <strong>AMBOS</strong> (jefe y Bienestar), agrega dos pasos en el orden deseado.
            </p>
            <div className="flex flex-col gap-2">
              {b.flow.map((s, i) => (
                <div key={i} className="flex flex-wrap items-end gap-2">
                  <span className="pb-2 text-2 text-muted">{i + 1}.</span>
                  <div className="min-w-40 flex-1">
                    <label className="sr-only" htmlFor={`st-flow-${i}`}>Etiqueta del paso {i + 1}</label>
                    <Entrada id={`st-flow-${i}`} placeholder="Etiqueta del paso" value={s.label}
                           onChange={(e) => set('flow', b.flow.map((x, j) =>
                             (j === i ? { ...x, label: e.target.value } : x)))} />
                  </div>
                  <div className="w-44">
                    <label className="sr-only" htmlFor={`st-flow-tipo-${i}`}>Quién aprueba el paso {i + 1}</label>
                    <Lista id={`st-flow-tipo-${i}`} value={s.approver_type}
                            onChange={(e) => set('flow', b.flow.map((x, j) =>
                              (j === i ? { ...x, approver_type: e.target.value as PasoDelFlujo['approver_type'] } : x)))}>
                      {APROBADORES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </Lista>
                  </div>
                  <Button size="sm" variant="ghost" aria-label={`Quitar el paso ${i + 1}`}
                          onClick={() => set('flow', b.flow.filter((_, j) => j !== i))}>
                    <i className="bi bi-x-lg text-danger-ink" aria-hidden="true" />
                  </Button>
                </div>
              ))}
            </div>
            <Button size="sm" variant="secondary" className="mt-3"
                    onClick={() => set('flow', [...b.flow, { label: '', approver_type: 'bienestar' }])}>
              <i className="bi bi-plus" aria-hidden="true" /> Agregar paso
            </Button>
          </section>
        </div>
      )}
    </Drawer>
  );
}

/** Una fila del constructor de campos. Solo enseña los ajustes que ese tipo de
 *  campo admite: un `min. caracteres` junto a un selector de archivo es ruido
 *  que hay que leer para descartar. */
function FilaDeCampo({
  campo: f, indice: i, todos, catalogos, autollenados, onCambio, onBorrar,
}: {
  campo: CampoDeFormulario;
  indice: number;
  todos: CampoDeFormulario[];
  catalogos: { key: string; label: string }[];
  autollenados: { key: string; label: string }[];
  onCambio: (k: keyof CampoDeFormulario, v: unknown) => void;
  onBorrar: () => void;
}) {
  const num = (v: string) => (v === '' ? null : parseInt(v, 10));
  const dec = (v: string) => (v === '' ? null : parseFloat(v));
  const esTexto = ['text', 'textarea', 'email', 'phone'].includes(f.type);

  return (
    <div className="rounded-sm border border-line bg-sunken p-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-44 flex-1">
          <label className="sr-only" htmlFor={`st-f-${i}-label`}>Etiqueta del campo {i + 1}</label>
          <Entrada id={`st-f-${i}-label`} placeholder="Etiqueta" value={f.label}
                 onChange={(e) => onCambio('label', e.target.value)} />
        </div>
        <div className="w-40">
          <label className="sr-only" htmlFor={`st-f-${i}-tipo`}>Tipo del campo {i + 1}</label>
          <Lista id={`st-f-${i}-tipo`} value={f.type}
                 onChange={(e) => onCambio('type', e.target.value)}>
            {TIPOS_DE_CAMPO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Lista>
        </div>
        <label className="flex min-h-9 cursor-pointer items-center gap-1.5 text-2 text-ink-2">
          <input type="checkbox" checked={Boolean(f.required)}
                 onChange={(e) => onCambio('required', e.target.checked)}
                 className="size-4 accent-[var(--color-brand)]" />
          Obligatorio
        </label>
        <Button size="sm" variant="ghost" aria-label={`Quitar el campo ${i + 1}`} onClick={onBorrar}>
          <i className="bi bi-x-lg text-danger-ink" aria-hidden="true" />
        </Button>
      </div>

      {/* Rejilla y no `flex-wrap`: cada ajuste lleva su ayuda debajo y las
          ayudas miden distinto, así que envolviendo quedaban escalonados. Con
          columnas de ancho fijo se alinean solos y el ancho deja de ser una
          propiedad de cada uno. */}
      <div className="mt-3 grid items-start gap-3
                      [grid-template-columns:repeat(auto-fill,minmax(11rem,1fr))]">
        {['select', 'radio', 'checkbox'].includes(f.type) && (
          <Ajuste etiqueta="Opciones (coma)">
            <Input value={(f.options ?? []).join(', ')}
                   onChange={(e) => onCambio('options',
                     e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
          </Ajuste>
        )}

        {['select', 'radio'].includes(f.type) && (
          <Ajuste etiqueta="Origen de las opciones"
                  ayuda="Un catálogo del sistema se actualiza solo: si mañana crean una sede, aparece sin editar el tipo.">
            <Select value={f.catalog_source ?? ''}
                    onChange={(e) => onCambio('catalog_source', e.target.value || null)}>
              <option value="">— Opciones manuales —</option>
              {catalogos.map((c) => <option key={c.key} value={c.key}>Catálogo: {c.label}</option>)}
            </Select>
          </Ajuste>
        )}

        {f.type === 'date' && (
          <>
            <Ajuste etiqueta="Mín. días desde hoy"
                    ayuda="0 = hoy (no permite pasado). Vacío = sin límite. Negativo = permite pasado.">
              <Input type="number" value={f.date_min ?? ''}
                     onChange={(e) => onCambio('date_min', num(e.target.value))} />
            </Ajuste>
            <Ajuste etiqueta="Máx. días desde hoy">
              <Input type="number" value={f.date_max ?? ''}
                     onChange={(e) => onCambio('date_max', num(e.target.value))} />
            </Ajuste>
            <Ajuste etiqueta="No antes de"
                    ayuda="Exige que esta fecha sea igual o posterior a otra del formulario.">
              <Select value={f.date_after ?? ''}
                      onChange={(e) => onCambio('date_after', e.target.value || null)}>
                <option value="">— Sin comparar —</option>
                {todos.map((o, j) => (j !== i && o.type === 'date' && o.key
                  ? <option key={o.key} value={o.key}>≥ {o.label || o.key}</option> : null))}
              </Select>
            </Ajuste>
          </>
        )}

        {f.type === 'rango_fechas' && (
          <>
            <Ajuste etiqueta="Duración mín. (días)">
              <Input type="number" value={f.dias_min ?? ''}
                     onChange={(e) => onCambio('dias_min', num(e.target.value))} />
            </Ajuste>
            <Ajuste etiqueta="Duración máx. (días)">
              <Input type="number" value={f.dias_max ?? ''}
                     onChange={(e) => onCambio('dias_max', num(e.target.value))} />
            </Ajuste>
            <Ajuste etiqueta="Inicio mín. desde hoy"
                    ayuda="0 = no permite pasado. Negativo = sí.">
              <Input type="number" value={f.date_min ?? ''}
                     onChange={(e) => onCambio('date_min', num(e.target.value))} />
            </Ajuste>
            <Ajuste etiqueta="Inicio máx. desde hoy">
              <Input type="number" value={f.date_max ?? ''}
                     onChange={(e) => onCambio('date_max', num(e.target.value))} />
            </Ajuste>
            <label className="flex min-h-9 cursor-pointer items-center gap-1.5 text-2 text-ink-2"
                   title="Impide que el mismo colaborador tenga dos solicitudes de ESTE tipo sobre los mismos días. No afecta a otros tipos.">
              <input type="checkbox" checked={f.sin_solape !== false}
                     onChange={(e) => onCambio('sin_solape', e.target.checked)}
                     className="size-4 accent-[var(--color-brand)]" />
              Sin solaparse
            </label>
          </>
        )}

        {f.type === 'number' && (
          <>
            <Ajuste etiqueta="Valor mín.">
              <Input type="number" value={f.min ?? ''}
                     onChange={(e) => onCambio('min', dec(e.target.value))} />
            </Ajuste>
            <Ajuste etiqueta="Valor máx.">
              <Input type="number" value={f.max ?? ''}
                     onChange={(e) => onCambio('max', dec(e.target.value))} />
            </Ajuste>
          </>
        )}

        {esTexto && (
          <>
            <Ajuste etiqueta="Mín. caracteres">
              <Input type="number" value={f.min_length ?? ''}
                     onChange={(e) => onCambio('min_length', num(e.target.value))} />
            </Ajuste>
            <Ajuste etiqueta="Máx. caracteres">
              <Input type="number" value={f.max_length ?? ''}
                     onChange={(e) => onCambio('max_length', num(e.target.value))} />
            </Ajuste>
            <Ajuste etiqueta="Patrón (regex)"
                    ayuda="Ej.: ^[0-9]{6,12}$ para una cédula.">
              <Input value={f.pattern ?? ''}
                     onChange={(e) => onCambio('pattern', e.target.value || null)} />
            </Ajuste>
          </>
        )}

        {['text', 'email', 'phone'].includes(f.type) && (
          <Ajuste etiqueta="Autollenado"
                  ayuda="El campo se llena solo con la ficha del colaborador y queda de solo lectura.">
            <Select value={f.autofill ?? ''}
                    onChange={(e) => onCambio('autofill', e.target.value || null)}>
              <option value="">— Lo escribe el colaborador —</option>
              {autollenados.map((a) => <option key={a.key} value={a.key}>Auto: {a.label}</option>)}
            </Select>
          </Ajuste>
        )}

        <Ajuste etiqueta="Ayuda para el colaborador">
          <Input value={f.help ?? ''} onChange={(e) => onCambio('help', e.target.value || null)} />
        </Ajuste>
      </div>
    </div>
  );
}

/** Un ajuste con su etiqueta VISIBLE.
 *
 *  En la versión vanilla el significado de cada casilla vivía en un `title` y en
 *  un `placeholder`: dos sitios que desaparecen —el `title` solo con el ratón
 *  quieto encima, el `placeholder` en cuanto se escribe algo—. Con doce
 *  recuadros numéricos en fila, quien volvía a editar un tipo tenía que pasar el
 *  ratón por cada uno para recordar cuál era cuál. */
function Ajuste({
  etiqueta, ayuda, children,
}: { etiqueta: string; ayuda?: string; children: React.ReactNode }) {
  return <Field label={etiqueta} ayuda={ayuda} className="min-w-0">{children}</Field>;
}

/* ── El reporte ────────────────────────────────────────────────────────── */

function PanelDeReporte({ tipo, onCerrar }: { tipo: TipoDeSolicitud | null; onCerrar: () => void }) {
  const consulta = useReporte(tipo?.id ?? null);
  const rep = consulta.data;

  return (
    <Drawer
      abierto={tipo != null}
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo={tipo ? `Reporte · ${tipo.name}` : 'Reporte'}
      descripcion={rep ? `${rep.total} solicitud${rep.total === 1 ? '' : 'es'}` : undefined}
      ancho="lg"
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cerrar</Button>
          <Button onClick={() => void Utils.downloadAuthed(
            `/api/solicitudes/types/${tipo?.id}/report/export`, '')}>
            <i className="bi bi-download" aria-hidden="true" /> Exportar Excel
          </Button>
        </>
      }
    >
      {consulta.isLoading && <Skeleton className="h-40 w-full" />}
      {consulta.isError && (
        <EstadoError err={consulta.error} que="el reporte"
                     onReintentar={() => void consulta.refetch()} />
      )}
      {rep && (
        <div className="overflow-x-auto rounded-sm border border-line">
          <table aria-label={`Reporte de ${tipo?.name ?? 'solicitudes'}`}
                 className="w-full border-collapse text-2">
            <thead>
              <tr className="border-b border-line bg-sunken text-left uppercase tracking-wide text-muted">
                {rep.columns.map((c) => (
                  <th scope="col" key={c.key} className="whitespace-nowrap px-3 py-2 font-semibold">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rep.rows.map((row, i) => (
                <tr key={i} className="border-b border-line last:border-0">
                  {/* Los valores los escribió el solicitante en campos libres.
                      La versión vanilla los metía en el HTML sin escapar. */}
                  {rep.columns.map((c) => (
                    <td key={c.key} className="px-3 py-2 text-ink-2">{row[c.key] ?? ''}</td>
                  ))}
                </tr>
              ))}
              {rep.rows.length === 0 && (
                <tr>
                  <td colSpan={rep.columns.length} className="px-3 py-6 text-center text-muted">
                    Sin datos
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Drawer>
  );
}
